import { Controller } from "@hotwired/stimulus"

// ConversationSearch — the "New message" recipient typeahead for the Messenger
// inbox rail (replaces the old <select>, which doesn't scale to an operator with
// 160+ tenants). Type-to-filter is SERVER-BACKED: a debounced GET to
// conversations#search returns a partial of team-scoped matches (Renter names
// are encrypted, so the server filters in Ruby via the same Renters::Filter the
// Tenants list uses). Each result is a button_to that POSTs to #create
// (find-or-create), exactly as the old picker did.
//
// Keyboard-accessible combobox: ↑/↓ move the active option, Enter selects it,
// Esc closes the panel. Clicking a result also works.
//
// Targets: panel (the collapsible search area), input, results (the <ul>),
// option (each result row, refreshed after every fetch).
export default class extends Controller {
  static targets = ["panel", "input", "results", "option"]

  connect() {
    this.activeIndex = -1
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // Reveal / hide the search panel; focus the input when opening.
  toggle() {
    const hidden = this.panelTarget.classList.toggle("hidden")
    if (!hidden) {
      requestAnimationFrame(() => this.inputTarget.focus())
    } else {
      this.reset()
    }
  }

  // Debounced server search. An empty query clears results (no request).
  search() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    const query = this.inputTarget.value.trim()

    this.debounceTimer = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    const url = new URL(this.searchUrl(), window.location.origin)
    url.searchParams.set("q", query)

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) return
      this.resultsTarget.innerHTML = await response.text()
      this.activeIndex = -1
      this.inputTarget.setAttribute("aria-expanded", "true")
    } catch (e) {
      // Swallow — a failed search just leaves the last results (or empty) shown.
    }
  }

  // Keyboard navigation over the current result options.
  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (this.activeIndex >= 0 && this.optionTargets[this.activeIndex]) {
          event.preventDefault()
          this.selectActive()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  move(delta) {
    const options = this.optionTargets
    if (options.length === 0) return

    this.activeIndex = (this.activeIndex + delta + options.length) % options.length
    options.forEach((el, i) => {
      const active = i === this.activeIndex
      el.querySelector("[data-active]")?.removeAttribute("data-active")
      const clickable = el.querySelector("button, a")
      if (clickable) {
        if (active) {
          clickable.setAttribute("data-active", "true")
          clickable.scrollIntoView({ block: "nearest" })
        } else {
          clickable.removeAttribute("data-active")
        }
      }
    })
  }

  // Submit the active option's button_to (POST → conversations#create).
  selectActive() {
    const option = this.optionTargets[this.activeIndex]
    const trigger = option?.querySelector("button")
    if (trigger) trigger.click()
  }

  close() {
    this.reset()
    this.panelTarget.classList.add("hidden")
  }

  reset() {
    this.activeIndex = -1
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.inputTarget.setAttribute("aria-expanded", "false")
  }

  searchUrl() {
    // Derived from the conversations index path (the controller lives on the
    // rail, always under /conversations). Kept relative-safe via a data attr
    // fallback so it works regardless of mount path.
    return this.element.dataset.conversationSearchUrl || "/conversations/search"
  }
}
